import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import SignaturePad from "@/components/SignaturePad";
import DatePickerField from "@/components/DatePickerField";
import { ArrowLeft, CheckCircle2, Send, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RichTextView } from "@/components/RichTextEditor";

// Heuristic: content authored with our RTE always contains at least one
// HTML tag. Legacy plain-text content has none, so we still render it
// with whitespace-pre-wrap for backward compatibility.
const looksLikeHtml = (s) => typeof s === "string" && /<\/?[a-z][\s\S]*>/i.test(s);

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * Standalone form-filling view. Used both from `/forms/:id/fill` (list) and
 * from the sign-in gate (via the same URL). Handles all field types,
 * enforces required, captures signature, POSTs, then redirects home.
 *
 * The `embedded` variant strips the outer chrome so the gate can wrap it in
 * a modal without duplicating the header/back button.
 */
export default function FormFill({ formIdOverride, onCompleted, embedded = false, previewMode = false, previewForm = null }) {
  const params = useParams();
  const formId = formIdOverride || params.id;
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [sig, setSig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Preview mode uses an in-memory form definition — no API call needed.
    if (previewMode && previewForm) {
      setForm(previewForm);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/forms/${formId}`);
        setForm(data);
        // Pre-fill answers if user already submitted (allows re-signing)
        const subs = await api.get("/forms", { params: { production_id: data.production_id } }).catch(() => ({ data: [] }));
        const mine = subs.data.find((f) => f.id === formId);
        if (mine?.my_submission_id) {
          // Fetch submission via manager endpoint won't work for non-mgr; keep it simple — start fresh
        }
      } catch (e) {
        // Same reasoning as the builder — silent redirect on stale URLs.
        if (!embedded) nav("/forms", { replace: true });
      }
    })();
  }, [formId, nav, embedded, previewMode, previewForm]);

  if (!form) {
    return <div className="text-zinc-500 text-sm p-8">Loading…</div>;
  }

  const setAnswer = (fieldId, val) => setAnswers((a) => ({ ...a, [fieldId]: val }));

  const hasSignatureField = form.fields.some((f) => f.type === "signature");
  const missingRequired = form.fields
    .filter((f) => f.required && f.type !== "content" && f.type !== "signature")
    .find((f) => {
      const v = answers[f.id];
      if (v === undefined || v === null) return true;
      if (typeof v === "string" && !v.trim()) return true;
      if (v === false) return true;
      return false;
    });
  const missingSig = hasSignatureField && !sig?.value;

  const submit = async () => {
    if (missingRequired) {
      toast.error(`"${missingRequired.label}" is required`);
      return;
    }
    if (missingSig) {
      toast.error("Signature is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/forms/${form.id}/submit`, { answers, signature: sig });
      toast.success("Signed and submitted");
      if (onCompleted) onCompleted();
      else nav("/forms");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally { setSaving(false); }
  };

  const inner = (
    <>
      {form.description && (
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl whitespace-pre-wrap">{form.description}</p>
      )}
      <div className="mt-6 space-y-5" data-testid="form-fill-fields">
        {form.fields.map((f) => {
          const commonLabel = (
            <Label className="block mb-1.5">
              {f.label}
              {f.required && <span className="text-amber-400 ml-1" title="Required">*</span>}
            </Label>
          );
          if (f.type === "content") {
            return (
              <div key={f.id} data-testid={`fill-content-${f.id}`} className="rounded-md border border-white/10 bg-[#0A0A0A] p-4 text-zinc-300 leading-relaxed">
                {looksLikeHtml(f.content) ? (
                  <RichTextView html={f.content} />
                ) : (
                  <div className="text-sm whitespace-pre-wrap">{f.content}</div>
                )}
              </div>
            );
          }
          if (f.type === "text") {
            return (
              <div key={f.id}>
                {commonLabel}
                <Input
                  data-testid={`fill-text-${f.id}`}
                  value={answers[f.id] || ""}
                  onChange={(e) => setAnswer(f.id, e.target.value)}
                  className="bg-[#0A0A0A] border-white/10"
                />
              </div>
            );
          }
          if (f.type === "textarea") {
            return (
              <div key={f.id}>
                {commonLabel}
                <Textarea
                  data-testid={`fill-textarea-${f.id}`}
                  value={answers[f.id] || ""}
                  onChange={(e) => setAnswer(f.id, e.target.value)}
                  rows={4}
                  className="bg-[#0A0A0A] border-white/10"
                />
              </div>
            );
          }
          if (f.type === "checkbox") {
            return (
              <div key={f.id} className="flex items-start gap-2">
                <Checkbox
                  id={`fill-cb-${f.id}`}
                  data-testid={`fill-checkbox-${f.id}`}
                  checked={!!answers[f.id]}
                  onCheckedChange={(v) => setAnswer(f.id, !!v)}
                  className="mt-0.5"
                />
                <Label htmlFor={`fill-cb-${f.id}`} className="text-sm leading-snug cursor-pointer">
                  {f.label}
                  {f.required && <span className="text-amber-400 ml-1" title="Required">*</span>}
                </Label>
              </div>
            );
          }
          if (f.type === "radio") {
            return (
              <div key={f.id}>
                {commonLabel}
                <RadioGroup
                  value={answers[f.id] || ""}
                  onValueChange={(v) => setAnswer(f.id, v)}
                  className="space-y-1.5"
                  data-testid={`fill-radio-${f.id}`}
                >
                  {(f.options || []).map((opt) => (
                    <div key={opt} className="flex items-center gap-2">
                      <RadioGroupItem id={`${f.id}-${opt}`} value={opt} />
                      <Label htmlFor={`${f.id}-${opt}`} className="text-sm cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            );
          }
          if (f.type === "date") {
            return (
              <div key={f.id}>
                {commonLabel}
                <DatePickerField
                  testid={`fill-date-${f.id}`}
                  value={answers[f.id] || ""}
                  onChange={(v) => setAnswer(f.id, v)}
                  placeholder="Pick a date"
                />
              </div>
            );
          }
          if (f.type === "signature") {
            return (
              <div key={f.id} data-testid={`fill-sig-${f.id}`}>
                {commonLabel}
                <SignaturePad
                  mode={form.signature_mode || "either"}
                  value={sig}
                  onChange={setSig}
                  defaultName={user?.name || ""}
                />
              </div>
            );
          }
          return null;
        })}
      </div>

      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-[11px] text-zinc-500 max-w-md">
          By submitting, you confirm your answers and signature are accurate and legally binding.
        </div>
        {previewMode ? (
          <div
            data-testid="preview-mode-badge"
            className="text-[11px] uppercase tracking-widest px-3 py-2 rounded border border-white/10 text-zinc-500"
            title="Submissions are disabled in preview mode"
          >Preview — submissions disabled</div>
        ) : (
          <button
            data-testid="submit-form-btn"
            onClick={submit}
            disabled={saving}
            style={goldBtn}
            className="h-11 px-6 rounded font-semibold text-sm hover:brightness-110 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? "Submitting…" : <><Send className="w-4 h-4" /> Sign &amp; submit</>}
          </button>
        )}
      </div>
    </>
  );

  if (embedded) return <div className="space-y-4">{inner}</div>;

  return (
    <div className="space-y-4 animate-fade-up max-w-3xl" data-testid="form-fill-page">
      <button
        onClick={() => nav("/forms")}
        className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1"
      ><ArrowLeft className="w-3 h-3" /> Back to Forms</button>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight font-light">{form.title}</h1>
        </div>
        {form.is_required && (
          <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/30 rounded px-2 py-1 inline-flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Required
          </span>
        )}
      </div>
      {inner}
    </div>
  );
}
